/**
 * Properties Screen (Authenticated)
 *
 * Simplified version using SpacesSearchView component
 * Shows property details in a modal when clicked
 */

import React, { useState } from "react";
import SpacesSearchView from "../../components/SpacesSearchView";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../shadcn/ui/dialog";
import imgplaceholder from "../../assets/img/Web/Spacia/imgplaceholder.png";

const Properties = () => {
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mainImage, setMainImage] = useState(null);

  const handleSpaceClick = space => {
    setSelectedSpace(space);
    setMainImage(space.images?.[0] || space.image || imgplaceholder);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSpace(null);
    setMainImage(null);
  };

  return (
    <>
      <SpacesSearchView
        mode="authenticated"
        showBackButton={false}
        title="Spaces & Services"
        subtitle="Manage and view your workspace listings"
        pageSize={10}
        onSpaceClick={handleSpaceClick}
        showPublishedStatusFilter={true}
      />

      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {selectedSpace && (
            <>
              <DialogHeader>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-4">
                    <DialogTitle className="text-2xl font-semibold">{selectedSpace.name || "Property"}</DialogTitle>
                    <div className="px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full whitespace-nowrap">
                      {selectedSpace.category?.name || selectedSpace.spaceType || "Space"}
                    </div>
                  </div>
                  <DialogDescription className="flex flex-wrap gap-2 items-center text-sm">
                    <div className="flex items-center text-green-600">
                      <svg className="mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span>{selectedSpace.status === "PUBLISHED" ? "Published" : "Unpublished"}</span>
                    </div>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-600">
                      {selectedSpace.subCategory?.name || selectedSpace.description || "Space"}
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">
                      {selectedSpace.location?.address || selectedSpace.locationString || "No address"}
                    </span>
                  </DialogDescription>
                </div>
              </DialogHeader>

              {/* Property Images */}
              <div className="mt-6">
                <div className="flex flex-col gap-4 md:flex-row">
                  <div className="md:w-3/4">
                    <div className="overflow-hidden h-72 bg-gray-100 rounded-lg md:h-96">
                      <img
                        src={mainImage}
                        alt={selectedSpace.name}
                        className="object-cover w-full h-full"
                        onError={e => {
                          e.target.src = imgplaceholder;
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex overflow-x-auto flex-row gap-2 md:w-1/4 md:flex-col md:overflow-y-auto md:max-h-96">
                    {selectedSpace.images && selectedSpace.images.length > 0 ? (
                      selectedSpace.images.map((img, index) => (
                        <div key={index} className="flex-shrink-0 w-24 h-24 md:w-full md:h-28">
                          <img
                            onClick={() => setMainImage(img)}
                            className="object-cover w-full h-full rounded-lg transition-all cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-500"
                            src={img}
                            alt={`Property ${index + 1}`}
                            onError={e => {
                              e.target.src = imgplaceholder;
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center justify-center w-24 h-24 md:w-full md:h-28 bg-gray-100 rounded-lg">
                        <span className="text-gray-400 text-xs">No images</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="mt-6 flex flex-col lg:flex-row gap-6">
                <div className="lg:w-2/3">
                  {/* Space Type */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">
                      {selectedSpace.subCategory?.name || selectedSpace.category?.name || "Space Details"}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <span>Capacity: {selectedSpace.capacity || "N/A"}</span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                        <span>Size: {selectedSpace.size || "N/A"}</span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                        <span>
                          Instances: {selectedSpace.instanceCount || selectedSpace.availableInstancesCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold mb-3">Description</h4>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedSpace.description || "No description available"}
                    </p>
                  </div>

                  {/* Amenities */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3">Amenities</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedSpace.amenities && selectedSpace.amenities.length > 0 ? (
                        selectedSpace.amenities.map((amenity, index) => (
                          <div
                            key={index}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                          >
                            {amenity.name || amenity.label || amenity}
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No amenities listed</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing Card */}
                <div className="lg:w-1/3">
                  <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 sticky top-4">
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-gray-900">
                        {selectedSpace.currency || "KES"} {selectedSpace.ratePrice || "N/A"}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Per Hour</div>
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="w-full px-4 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md hover:shadow-lg"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Properties;
